import {
  AUTH_EXPIRED_EVENT,
  EMAIL_NOT_VERIFIED_EVENT,
  createTroveApiClient,
  type FreshTokenResult,
} from '@trove/api-client';
import {
  clearClientActiveAid,
  getClientActiveAid,
  setClientActiveAid,
} from '@trove-shared/workspace/activeAid';
import { auth } from './firebase';
import { getSwAuthUser } from './extension/chromeAuthBridge';

export const WORKSPACE_ACCESS_DENIED_EVENT = 'trove:workspace-access-denied';

export {
  AUTH_EXPIRED_EVENT,
  EMAIL_NOT_VERIFIED_EVENT,
  EMAIL_VERIFICATION_MESSAGE_KEY,
  EXTENSION_RETURN_PATH_KEY,
  REDIRECT_VERIFY_EMAIL_KEY,
  AUTH_ERROR_MESSAGE_KEY,
  isAxiosUnauthorizedError,
  isTransientAuthFailure,
  getMessageFromResponse,
  getAxiosErrorResponseData,
  parseStructuredApiError,
  humanizeApiErrorBody,
  isAxiosGenericStatusMessage,
  isEmailNotVerifiedResponse,
  isEmailNotVerifiedMessage,
  WORKSPACE_ACCESS_DENIED_CODE,
  PLAN_FEATURE_DISABLED_CODE,
  QUOTA_EXCEEDED_CODE,
  isWorkspaceAccessDeniedResponse,
} from '@trove/api-client';

export {
  getClientActiveAid,
  setClientActiveAid,
  clearClientActiveAid,
  hydrateClientActiveAidFromSw,
  subscribeActiveAidChanges,
} from '@trove-shared/workspace/activeAid';

const isExtension =
  typeof window !== 'undefined' && window.location.protocol === 'chrome-extension:';

function isTransientFirebaseTokenError(error: unknown): boolean {
  const code = (error as { code?: string })?.code;
  return code === 'auth/network-request-failed' || code === 'auth/internal-error';
}

const getAuthTokenFromExtension = (): Promise<string | null> => {
  console.log('[API] Extension context — requesting token from service worker');
  return new Promise((resolve) => {
    try {
      (
        window as unknown as {
          chrome: {
            runtime: {
              sendMessage: (
                msg: { action: string },
                cb: (response: { token?: string }) => void
              ) => void;
            };
          };
        }
      ).chrome.runtime.sendMessage({ action: 'getToken' }, (response) => {
        const token = response?.token ?? null;
        console.log('[API] SW token response:', token ? `token(${token.length} chars)` : 'null');
        resolve(token);
      });
    } catch (err) {
      console.error('[API] sendMessage failed:', err);
      resolve(null);
    }
  });
};

const getAuthToken = (): Promise<string | null> => {
  if (isExtension) {
    return getAuthTokenFromExtension();
  }
  const token = auth.currentUser?.getIdToken() ?? Promise.resolve(null);
  console.log('[API] Web context — using Firebase currentUser:', auth.currentUser?.email ?? 'null');
  return token;
};

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Extension token refresh. The service worker owns Firebase, and MV3 tears it
 * down after ~30s idle. A request that races the SW cold start can get a null
 * token for a beat even though the session is perfectly valid. Retry a few
 * times, then ask the SW whether a user actually exists before concluding the
 * session is gone — otherwise a transient worker hiccup is misread as a logout
 * and the embedded app bounces profile -> login -> home.
 */
const getFreshTokenFromExtension = async (): Promise<FreshTokenResult> => {
  for (let attempt = 0; attempt < 3; attempt++) {
    const token = await getAuthTokenFromExtension();
    if (token) return { kind: 'token', token };
    if (attempt < 2) await delay(250 * (attempt + 1));
  }
  // Still no token after retries. Only treat as a real logout when the SW
  // positively confirms there is no user; unreachable SW or a still-present
  // session is transient (keep the user signed in).
  const swState = await getSwAuthUser();
  if (!swState.ok || swState.user) {
    return {
      kind: 'transient',
      cause: new Error('Extension auth token temporarily unavailable (service worker warming up).'),
    };
  }
  return { kind: 'no-session' };
};

const getFreshToken = async (): Promise<FreshTokenResult> => {
  try {
    if (isExtension) {
      return await getFreshTokenFromExtension();
    }
    if (!auth.currentUser) return { kind: 'no-session' };
    const token = await auth.currentUser.getIdToken(/* forceRefresh */ true);
    return { kind: 'token', token };
  } catch (e) {
    if (isExtension) {
      // Never hard-sign-out from an unexpected messaging error in the extension.
      return { kind: 'transient', cause: e };
    }
    if (isTransientFirebaseTokenError(e)) {
      return { kind: 'transient', cause: e };
    }
    return { kind: 'no-session' };
  }
};

const verificationStore =
  typeof window !== 'undefined' && typeof sessionStorage !== 'undefined'
    ? sessionStorage
    : undefined;

const api = createTroveApiClient({
  baseURL:
    import.meta.env.VITE_API_BASE_URL ||
    'http://localhost:5001/trovepile-39142/us-central1/api',
  getAuthToken,
  getFreshToken,
  onAuthExpired: (detail) => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT, { detail }));
  },
  onEmailNotVerified: (message) => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(EMAIL_NOT_VERIFIED_EVENT, { detail: { message } }));
  },
  getActiveAid: getClientActiveAid,
  onWorkspaceAccessDenied: ({ defaultAid, message }) => {
    if (typeof window === 'undefined') return;
    clearClientActiveAid();
    if (defaultAid) {
      void setClientActiveAid(defaultAid);
    }
    window.dispatchEvent(
      new CustomEvent(WORKSPACE_ACCESS_DENIED_EVENT, {
        detail: { defaultAid, message },
      })
    );
  },
  verificationStore,
});

export default api;
